var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import Tooltip from 'devextreme/ui/tooltip';
import { v4 as uuidv4 } from 'uuid';
import { msg, updateWhenLocaleChanges } from '@lit/localize';
import { DinapsisChart } from '../chart/dinapsis-chart';
import { BaseShadowless } from './BaseShadowless';
/**
 * Represents the indicator card
 */
let DinapsisIndicator = class DinapsisIndicator extends BaseShadowless {
    constructor() {
        super();
        /**
         * A property that is used to set the card height
         */
        this.height = '174px';
        /**
         * A property that is used to set the card width
         */
        this.width = '250px';
        /**
         * A property that is used to set the icon of the card
         */
        this.topIcon = '';
        /**
         * A property that is used to set the icon wich show in the left of the card
         */
        this.leftIcon = {
            name: '',
            backgroundColor: '',
            color: '',
        };
        /* A private property that is used to set the default color of the left icon. */
        this.leftIconColorDefault = '#fff';
        /* A private property that is used to set the default background color of the left icon. */
        this.leftIconBackgroundColorDefault = '#e64949';
        /**
         * A property that is used to set the icon wich show in the left of the card.
         */
        this.backgroundIcon = {
            name: '',
            color: '',
            size: '',
        };
        /* A private property that is used to set the default color of the background icon. */
        this.backgroundIconDefaultColor = '#000';
        /* A private property that is used to set the default size of the background icon. */
        this.backgroundIconDefaultSize = '105px';
        /**
         * A property that is used to set if the card is clickable or not
         */
        this.clickable = false;
        /**
         * A property that is used to set the indicator data
         */
        this.indicatorData = {
            title: '',
            dataUnit: '',
            dataDetail: '',
        };
        /**
        * Property used to set the text that will appear if there isn't data for the 'dataUnit' property.
        * Default value is 'sin datos'
        */
        this.textNoData = '';
        /**
        * Property used to set the color of texts and icons when there isn't data.
        * Default value is #e64949.
        */
        this.colorNoData = '#e64949';
        /**
        * Property used to set an icon in the chart area when there isn't data.
        * Only accepts values from the Material iconographic library. Default value is warning.
        */
        this.iconNoChartData = 'warning';
        /**
         * Property used to set a text in the chart area when there isn't data.
         * Default value is 'Gráfica sin datos'.
         */
        this.textNoChartData = '';
        /**
         * A property that is used to set the chart data
         */
        this.chartData = [];
        /**
         * A property that is use to set a complementary text on the top of the card
         */
        this.complementaryTextTop = '';
        /**
         * A property that is use to set a complementary text under the DataUnit box
         */
        this.complementaryTextUnitDetail = '';
        /**
         * A property that is use to set a complementary text on the left bottom if chart is active
         */
        this.complementaryTextBottomChart = '';
        /**
         * A property that is use to set a complementary icon on the left bottom if chart is active
         */
        this.complementaryIconBottomChart = '';
        /**
         * A property that is use to set the complementary icon color on the left bottom
         * if chart is active
         */
        this.complementaryIconBottomChartColor = '';
        /**
         * A property that is use to set a complementary text on the right bottom
         */
        this.complementaryTextBottomRight = '';
        /**
         * A property that is use to set a complementary icon on the right bottom if chart is active
         */
        this.complementaryIconBottomChartRight = '';
        /**
         * A property that is use to set the complementary icon color on the right bottom
         * if chart is active
         */
        this.complementaryIconBottomChartColorRight = '';
        /**
         * A property that is used to set the component to show in mode card or not
         */
        this.isCard = true;
        /**
         * A private variable for set unique id for tooltips
         */
        this.tooltipRandomId = uuidv4();
        /**
         * A property that is used to set the name of slot to set inside the card
         */
        this.slotName = 'card-container';
        updateWhenLocaleChanges(this);
    }
    /**
     * This function returns an object that maps the names of all custom elements that are defined in
     * this component to their constructors.
     * @returns The return value is an object that maps the names of the custom elements to their
     * constructors.
     */
    static get scopedElements() {
        return {
            'dinapsis-chart': DinapsisChart,
        };
    }
    /**
     * The createRenderRoot function is a function that is called by the lit-html library to tell the
     * component where to render the html.
     * @returns The element itself.
     */
    createRenderRoot() {
        return this;
    }
    /**
     * A function that emit an event when the card icon is clicked
     */
    handleClick(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('click-icon'));
    }
    /**
     * A function that emit an event when the card is clicked
     */
    handleCardClick(e) {
        e.stopPropagation();
        if (!this.clickable)
            return;
        this.dispatchEvent(new Event('click'));
    }
    /**
     * A function that check if have chart data
     */
    haveChart() {
        return this.chartData.length > 0;
    }
    /**
     * A function that recive one element and check if the text overflow or not.
     * @param el This param represents the element that will be checked if it overflows.
     * @returns Returns true if the element overflow and false if it doesn't.
     */
    checkOverflow(el) {
        const curOverflow = el.style.overflow;
        const element = el;
        const isOverflowing = el.clientWidth < el.scrollWidth;
        element.style.overflow = curOverflow;
        return isOverflowing;
    }
    /**
     * A function that recives two params and generate a tooltip.
     * @param tooltipId This param represents the id of the tooltip.
     * @param target This param represents the container the tooltip points to.
     */
    generateTooltip(tooltipId, target) {
        // Select and remove the previous tooltip
        const tooltipContainer = document.getElementById(tooltipId);
        if (tooltipContainer)
            tooltipContainer.remove();
        // Select the container where the tooltip will be painted
        const targetContainer = document.getElementById(target);
        if (!targetContainer)
            return;
        const txtTarget = targetContainer.textContent;
        // Create the tooltip div and set id and target text
        const newTooltipContainer = document.createElement('div');
        newTooltipContainer.id = tooltipId;
        newTooltipContainer.innerHTML = txtTarget !== null && txtTarget !== void 0 ? txtTarget : '';
        // Add to target the tooltip container
        targetContainer.append(newTooltipContainer);
        // Check if target needed tooltip and set or remove class
        if (targetContainer && !this.checkOverflow(targetContainer)) {
            newTooltipContainer.classList.add('hide-tooltip');
        }
        else {
            newTooltipContainer.classList.remove('hide-tooltip');
        }
        // Check if exist target, tooltip and tooltip doesn't have hide-tooltip class
        if (targetContainer
            && newTooltipContainer
            && !(newTooltipContainer === null || newTooltipContainer === void 0 ? void 0 : newTooltipContainer.classList.contains('hide-tooltip'))) {
            const tooltip = new Tooltip(newTooltipContainer);
            tooltip.option('target', targetContainer);
            tooltip.option('showEvent', 'mouseenter');
            tooltip.option('hideEvent', 'mouseleave');
        }
    }
    /**
     * A function that setup the tooltips of the component
     */
    setupTooltips() {
        const container = document.getElementById(`indicator-container-${this.tooltipRandomId}`);
        const tooltipOptions = [
            {
                tooltipId: `tooltip-indicator-title-${this.tooltipRandomId}`,
                target: `indicator-card-top__title-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-data-unit-${this.tooltipRandomId}`,
                target: `indicator-data-unit-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-data-detail-${this.tooltipRandomId}`,
                target: `indicator-data-detail-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-unit-detail-${this.tooltipRandomId}`,
                target: `complementary-text-unit-detail-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-top-text-${this.tooltipRandomId}`,
                target: `complementary-text-top-text-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-bottom-chart-${this.tooltipRandomId}`,
                target: `complementary-text-bottom-chart-${this.tooltipRandomId}`,
            },
            {
                tooltipId: `tooltip-indicator-bottom-right-${this.tooltipRandomId}`,
                target: `complementary-text-bottom-right-${this.tooltipRandomId}`,
            },
        ];
        if (container) {
            tooltipOptions.forEach((tooltip) => {
                this.generateTooltip(tooltip.tooltipId, tooltip.target);
            });
        }
    }
    /**
     * The above function is a lifecycle hook that is called when the component is updated.
     * It is used to initialize the tree view.
     */
    updated() {
        this.setupTooltips();
    }
    /**
     * A function that render a div with the title of indicator
     * @returns Returns a html template.
     */
    indicatorTitle() {
        if (this.indicatorData.title === '')
            return '';
        return html `
      <span class="indicator-title" id="indicator-title-${this.tooltipRandomId}">
        ${this.indicatorData.title}
      </span>
    `;
    }
    /**
     * A function that render a div with an icon.
     * @returns Returns a html template.
     */
    indicatorTopIconRight() {
        if (this.topIcon === '')
            return '';
        return html `
      <div>
        <div class="indicator-card-icon">
          <span
            class="material-icons-round indicator-icon indicator-icon--top"
            @click=${this.handleClick}
          >
            ${this.topIcon}
          </span>
        </div>
      </div>
    `;
    }
    /**
     * If the leftIcon.color property exists, return it, otherwise return the default color.
     * @returns The color of the icon.
     */
    selectColorLeftIcon() {
        return this.leftIcon.color
            ? this.leftIcon.color
            : this.leftIconColorDefault;
    }
    /**
     * If the leftIcon.backgroundIcon is defined, then return that value, otherwise return the
     * default value.
     * @returns The background color of the left icon.
     */
    selectBackgroundColorLeftIcon() {
        return this.leftIcon.backgroundColor
            ? this.leftIcon.backgroundColor
            : this.leftIconBackgroundColorDefault;
    }
    /**
     * If the leftIcon.name is empty or there is no chart,
     * return an empty string. Otherwise, return a div
     * with a background color and a icon with a color.
     * @returns The return is a template literal.
     */
    indicatorCardLeftIcon() {
        if (!this.leftIcon.name) {
            return '';
        }
        return html `
      <div
        class="indicator-card-icon"
        style="background-color: ${this.selectBackgroundColorLeftIcon()};"
      >
        <span
          class="material-icons-round indicator-icon left-icon"
          style="color: ${this.selectColorLeftIcon()};"
        >
          ${this.leftIcon.name}
        </span>
      </div>
    `;
    }
    /**
     * If the icon is empty, return an empty string, otherwise return the icon.
     * @param {String} icon - String
     * @returns The return value is a string.
     */
    createIconStructure(icon, color, idText) {
        if (icon === '')
            return '';
        return html `
      <div class="indicator-card-icon" id="indicator-card-icon-${idText}">
        <span
          class="material-icons-round indicator-icon indicator-icon--bottom"
          style="color: ${color}"
        >
          ${icon}
        </span>
      </div>
    `;
    }
    /**
     * It creates a div with a complementary text
     * @param {String} [text] - The text to be displayed
     * @param {String} [icon] - The name of icon to be displayed
     * @param {String} [idText] - The id of the text
     * @returns A string of HTML.
     */
    createComplementaryText(text = '', icon = '', idText = '', iconColor = '', customClass) {
        if (text === '')
            return '';
        const haveIcon = icon !== '';
        const id = idText !== '' ? idText : uuidv4();
        const iconStructure = haveIcon ? this.createIconStructure(icon, iconColor, idText) : '';
        const additionalContainerIcon = haveIcon
            ? 'complementary-text-container--icon'
            : '';
        return html ` <div
      class="complementary-text-container ${additionalContainerIcon} ${customClass}"
    >
      ${iconStructure}
      <span class="complementary-text" id="complementary-text-${id}-${this.tooltipRandomId}">
        ${text}
      </span>
    </div>`;
    }
    /**
     * A function that render a div container with all structure to create top container.
     * If it is not a card, nothing is returned
     * @returns Returns a html template.
     */
    generateTopIndicator() {
        if (!this.isCard)
            return html ``;
        return html `
      <div class="indicator-card-top">
        <div class="indicator-card-top__title" id="indicator-card-top__title-${this.tooltipRandomId}">
          ${this.indicatorTitle()}
        </div>
        <div>${this.indicatorTopIconRight()}</div>
      </div>
    `;
    }
    /**
     * If the dataUnit property of the indicatorData object is an empty string,
     * return an empty string.
     * Otherwise, return a span element with the dataUnit property as
     * its text content, and a div element
     * with the dataUnit property as its text content.
     * @returns a string of HTML.
     */
    generateDataUnit() {
        const textNodata = this.textNoData === '' ? msg('text_no_data') : this.textNoData;
        return html `
      <span class="indicator-data-unit" id="indicator-data-unit-${this.tooltipRandomId}" style="color: ${this.indicatorData.dataUnit === '' ? this.colorNoData : ''}">
        ${this.indicatorData.dataUnit === '' ? textNodata : this.indicatorData.dataUnit}
      </span>
    `;
    }
    /**
     * If the indicatorData.dataDetail property is not an empty string,
     * then return a span element with
     * the indicatorData.dataDetail property as the text content, and a div element with the
     * indicatorData.dataDetail property as the text content.
     * @returns The return value is a string.
     */
    generateDataDetail() {
        if (this.indicatorData.dataDetail === '')
            return '';
        return html `
      <span class="indicator-data-detail" id="indicator-data-detail-${this.tooltipRandomId}">
        ${this.indicatorData.dataDetail}
      </span>
    `;
    }
    /**
     * A function that print the indicator container.
     * @returns Returns a html template.
     */
    createIndicatorContainer() {
        const topText = this.createComplementaryText(this.complementaryTextTop, '', 'top-text', '');
        const dataUnit = this.generateDataUnit();
        const dataDetail = this.generateDataDetail();
        const indicatorIcon = this.indicatorCardLeftIcon();
        const indicatorBackground = this.createIconBackgroundStructure();
        const container = indicatorIcon !== '' ? indicatorIcon : indicatorBackground;
        const complementaryText = this.createComplementaryText(this.complementaryTextUnitDetail, '', 'unit-detail', '');
        const haveChart = this.haveChart();
        const haveLeftIcon = indicatorIcon !== '';
        const dataContainerClass = haveLeftIcon ? 'side-by-side' : 'full-width';
        const contentClass = haveLeftIcon || haveChart ? 'with-content' : 'without-content';
        return html `
      <div class="indicator-data-container indicator-data-container-${dataContainerClass} ${contentClass}">
        <div class="indicator-unitdetail-container">
          ${topText}
          ${dataUnit} ${dataDetail}
          ${complementaryText}
        </div>
      </div>
      <div class="indicator-left-icon-container ${contentClass}">${container}</div>
      ${haveChart ? this.createChartStructure() : this.createSlotStructure()}
    `;
    }
    /**
     * If the slotName property is defined,
     * then render the slotName property as a shadowless component.
     * @returns The slotName is being returned.
     */
    createSlotStructure() {
        if (this.slotName) {
            return this.shadowlessRender(this.slotName);
        }
        return '';
    }
    /**
     * If the indicatorData object has a property called haveChart, then return an empty string.
     * Otherwise, return the html template literal with chart indicator component.
     * @returns A string of HTML.
     */
    createChartStructure() {
        if (!this.haveChart())
            return '';
        const haveData = this.chartData.some((x) => x.data.length > 0);
        if (!haveData) {
            const textNoChartData = this.textNoChartData === '' ? msg('text_no_chart_data') : this.textNoChartData;
            return html `
        <div class='no-chart-data container-no-data' style="color: ${this.colorNoData}">
          <span class="material-icons-round no-data-icon">${this.iconNoChartData}</span>
          <span class="no-data-text">${textNoChartData}</span>
      </div>`;
        }
        return html `
        <dinapsis-sparkline-chart
          style="width: inherit; height: inherit"
          .data="${this.chartData}"
          .chartOptions="${this.chartOptions}"
          @chart-loaded="${this.chartLoaded}"
        ></dinapsis-sparkline-chart>
      `;
    }
    /**
     * Add an event listener to listen for the 'chart-loaded' event
     * @param $event
     */
    chartLoaded($event) {
        this.dispatchEvent(new CustomEvent('chart-loaded', $event));
    }
    /**
     * A function that print the indicator container with background.
     * @returns A string of HTML.
     */
    createIconBackgroundStructure() {
        if (this.backgroundIcon.name === '')
            return '';
        const sizeIcon = this.backgroundIcon.size
            ? this.backgroundIcon.size
            : this.backgroundIconDefaultSize;
        const colorIcon = this.backgroundIcon.color
            ? this.backgroundIcon.color
            : this.backgroundIconDefaultColor;
        return html `
      <div style="width: inherit; height: inherit;">
        <div class="indicator-background-icon">
          <span
            class="material-icons-round background-icon"
            style="font-size: ${sizeIcon}; line-height: ${sizeIcon}; color: ${colorIcon};"
          >
            ${this.backgroundIcon.name}
          </span>
        </div>
      </div>
    `;
    }
    /**
     * A function that print the container of the indicator.
     * @returns A string of HTML.
     */
    generateContainer() {
        const bottomLeftTextChart = this.createComplementaryText(this.complementaryTextBottomChart, this.complementaryIconBottomChart, 'bottom-chart', this.complementaryIconBottomChartColor);
        const bottomRightText = this.createComplementaryText(this.complementaryTextBottomRight, this.complementaryIconBottomChartRight, 'bottom-right', this.complementaryIconBottomChartColorRight, 'justify-content-end');
        const multiple = bottomLeftTextChart !== '' && bottomRightText !== '';
        const classMultiple = multiple ? 'bottom-container--multiple' : 'bottom-container--single';
        const classChart = this.haveChart() ? 'complementary-text-bottom' : '';
        return html `
      <div class="indicator-child-container">

        <div class="indicator-child-container--group">
          ${this.createIndicatorContainer()}
        </div>
      </div>
      <div class="bottom-container ${classMultiple} ${classChart}">
        ${bottomLeftTextChart} ${bottomRightText}
      </div>
    `;
    }
    /**
     * The function returns a string of HTML that contains a div that contains the component.
     * @returns The return statement is returning the html template literal
     */
    render() {
        return html `
      <div
        class="
          indicator-container
          ${this.isCard ? 'dinapsis-card dinapsis-card-shadowed' : ''}
          ${this.clickable ? 'clickable-card' : ''}
        "
        id="indicator-container-${this.tooltipRandomId}"
        style="height: ${this.height}; width: ${this.width}"
        @click=${this.handleCardClick}
      >
        ${this.generateTopIndicator()} ${this.generateContainer()}
      </div>
    `;
    }
};
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "height", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "width", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "topIcon", void 0);
__decorate([
    property({ type: Object })
], DinapsisIndicator.prototype, "leftIcon", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "backgroundIcon", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisIndicator.prototype, "clickable", void 0);
__decorate([
    state(),
    property({ type: Object })
], DinapsisIndicator.prototype, "indicatorData", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "textNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "colorNoData", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "iconNoChartData", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "textNoChartData", void 0);
__decorate([
    property({ type: Array })
], DinapsisIndicator.prototype, "chartData", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryTextTop", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryTextUnitDetail", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryTextBottomChart", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryIconBottomChart", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryIconBottomChartColor", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryTextBottomRight", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryIconBottomChartRight", void 0);
__decorate([
    property({ type: String })
], DinapsisIndicator.prototype, "complementaryIconBottomChartColorRight", void 0);
__decorate([
    property({ type: Object })
], DinapsisIndicator.prototype, "chartOptions", void 0);
__decorate([
    property({ type: Boolean })
], DinapsisIndicator.prototype, "isCard", void 0);
DinapsisIndicator = __decorate([
    customElement('dinapsis-indicator')
], DinapsisIndicator);
export { DinapsisIndicator };
//# sourceMappingURL=dinapsis-indicator.js.map